/* UI sound effects, synthesized with WebAudio — no asset files, same philosophy
   as soundscape.ts. A small, cohesive sonic vocabulary: every cue is built from
   the same warm sine/triangle voice on a pentatonic palette, so the set sounds
   like one instrument rather than a grab-bag of stock dings.

   The grammar, so cues stay legible without looking:
     • self vs others — your own actions are warm & rounded (triangle, lower
       register); other people are brighter & quieter (sine, higher) so they sit
       in the background.
     • up = on/arrive/good, down = off/leave. Mute↔unmute, deafen↔undeafen,
       join↔leave are mirror pairs you can tell apart with eyes shut.
     • knock is the one deliberately memorable, literal motif: two wooden taps.

   Everything is short (< ~0.5s), low-gain, and gated by a per-cue throttle so a
   burst of chat or arrivals can't turn into a cacophony. Honoured live from
   prefs (toggle + volume); nothing plays when sound effects are off. */
import { usePrefs } from '@/stores/prefs'

export type Sfx =
  | 'selfJoin' | 'selfLeave'
  | 'mute' | 'unmute'
  | 'deafen' | 'undeafen'
  | 'peerJoin' | 'peerLeave'
  | 'knock' | 'message' | 'mention' | 'notify'

let ctx: AudioContext | null = null
let master: GainNode | null = null

// Per-cue throttle: a flurry of arrivals or messages shouldn't stack into noise.
const lastAt: Record<string, number> = {}
const MIN_GAP_MS = 70

// Scheduling head-start. A freshly-created/resumed context (or one busy under a
// call connect) needs a little runway, or the first note's attack ramp lands in
// the past and gets clipped — the "cut off at the start" the join cue showed.
const LEAD = 0.05

function audio(): { ac: AudioContext; out: GainNode } | null {
  try {
    if (!ctx) {
      ctx = new AudioContext()
      master = ctx.createGain()
      master.gain.value = 1
      master.connect(ctx.destination)
    }
    if (ctx.state !== 'running') void ctx.resume() // cues fire after a user gesture, so this is allowed
    return { ac: ctx, out: master! }
  } catch {
    return null
  }
}

/* Warm the audio hardware on the first user gesture so it's already running well
   before the first real cue (typically a call join). Without this, the very first
   sound of a session spins the context up mid-playback and the attack is clipped —
   which is exactly why the dev panel (clicked after other interactions) sounded
   clean while a fresh join didn't. */
let warmed = false
function warmup(): void {
  if (warmed) return
  warmed = true
  audio()
}
if (typeof window !== 'undefined') {
  const opts = { once: true, passive: true } as const
  window.addEventListener('pointerdown', warmup, opts)
  window.addEventListener('keydown', warmup, opts)
}

/* A single voice: an oscillator with a fast attack and exponential decay, plus a
   quiet octave partial for a touch of shimmer. The exponential tail is what makes
   it read as a soft "ping" rather than a beep. */
function tone(
  ac: AudioContext,
  dest: AudioNode,
  o: { freq: number; t: number; dur: number; type?: OscillatorType; gain?: number; attack?: number; glideTo?: number; partial?: number },
): void {
  const { freq, t, dur, type = 'sine', gain = 0.2, attack = 0.008, glideTo, partial = 0 } = o
  const g = ac.createGain()
  g.connect(dest)
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(gain, t + attack)
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur)

  const osc = ac.createOscillator()
  osc.type = type
  osc.frequency.setValueAtTime(freq, t)
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t + dur * 0.9)
  osc.connect(g)
  osc.start(t)
  osc.stop(t + dur + 0.02)

  if (partial > 0) {
    const og = ac.createGain()
    og.gain.value = partial
    og.connect(g)
    const oo = ac.createOscillator()
    oo.type = type
    oo.frequency.setValueAtTime(freq * 2, t)
    if (glideTo) oo.frequency.exponentialRampToValueAtTime(glideTo * 2, t + dur * 0.9)
    oo.connect(og)
    oo.start(t)
    oo.stop(t + dur + 0.02)
  }
}

/* One knuckle-on-wood rap. Deep and woody, but the whole rap runs through a
   gentle lowpass — like a knock heard through a door, which is naturally muffled.
   That rounds off the brittle high-frequency "crack" of the contact transient, so
   what's left is a soft, subtle thud rather than a sharp demand. Two layers:
     • a contact tap — a brief noise burst (tamed by the lowpass), and
     • a wooden body — two low partials whose pitch sags as they decay, for weight.
   `scale` humanises the raps so they aren't robotically identical. */
function rap(ac: AudioContext, dest: AudioNode, t: number, scale = 1): void {
  // a shared lowpass softens the whole rap — the "through a door" muffling
  const lp = ac.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 1150
  lp.Q.value = 0.5
  lp.connect(dest)

  // contact tap
  const len = Math.floor(ac.sampleRate * 0.05)
  const buf = ac.createBuffer(1, len, ac.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len)
  const src = ac.createBufferSource()
  src.buffer = buf
  const band = ac.createBiquadFilter()
  band.type = 'bandpass'
  band.frequency.value = 900
  band.Q.value = 0.8
  const cg = ac.createGain()
  cg.gain.setValueAtTime(0.0001, t)
  cg.gain.exponentialRampToValueAtTime(0.32 * scale, t + 0.005)
  cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.05)
  src.connect(band).connect(cg).connect(lp)
  src.start(t)
  src.stop(t + 0.06)

  // wooden body — two low partials, fundamental sags for a "tok"
  const body: [number, number, number][] = [[230, 185, 0.6], [380, 330, 0.28]]
  for (const [f0, f1, g0] of body) {
    const g = ac.createGain()
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(g0 * scale, t + 0.006)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22)
    const osc = ac.createOscillator()
    osc.type = 'triangle'
    osc.frequency.setValueAtTime(f0, t)
    osc.frequency.exponentialRampToValueAtTime(f1, t + 0.12)
    osc.connect(g).connect(lp)
    osc.start(t)
    osc.stop(t + 0.24)
  }
}

// Pentatonic-ish palette (Hz) — any two of these together stay consonant.
const E4 = 329.63, A4 = 440, C5 = 523.25, E5 = 659.25, G5 = 783.99, A5 = 880, D6 = 1174.66, G6 = 1567.98

// Roughly how long each cue occupies the speakers, in ms (last note's start +
// duration). playSfx() returns this so callers can avoid stepping on a cue —
// e.g. the call code holds mic acquisition until the join cue has finished.
const CUE_MS: Record<Sfx, number> = {
  selfJoin: 420, selfLeave: 440, mute: 220, unmute: 200, deafen: 350, undeafen: 330,
  peerJoin: 260, peerLeave: 280, knock: 620, message: 70, mention: 450, notify: 460,
}

const cues: Record<Sfx, (ac: AudioContext, out: AudioNode, t: number) => void> = {
  // self — warm triangle, lower register, two-note motifs you can mirror
  selfJoin: (ac, out, t) => { tone(ac, out, { freq: C5, t, dur: 0.18, type: 'triangle', gain: 0.22, partial: 0.12 }); tone(ac, out, { freq: G5, t: t + 0.1, dur: 0.32, type: 'triangle', gain: 0.24, partial: 0.12 }) },
  selfLeave: (ac, out, t) => { tone(ac, out, { freq: G5, t, dur: 0.16, type: 'triangle', gain: 0.22, partial: 0.1 }); tone(ac, out, { freq: C5, t: t + 0.1, dur: 0.34, type: 'triangle', gain: 0.22, partial: 0.1 }) },
  unmute: (ac, out, t) => { tone(ac, out, { freq: E5, t, dur: 0.07, type: 'triangle', gain: 0.2 }); tone(ac, out, { freq: A5, t: t + 0.06, dur: 0.14, type: 'triangle', gain: 0.22 }) },
  mute: (ac, out, t) => { tone(ac, out, { freq: A5, t, dur: 0.07, type: 'triangle', gain: 0.2 }); tone(ac, out, { freq: E5, t: t + 0.06, dur: 0.16, type: 'triangle', gain: 0.18 }) },
  // deafen — low and muffled, "covered"; undeafen opens back up
  deafen: (ac, out, t) => { tone(ac, out, { freq: A4, t, dur: 0.12, type: 'sine', gain: 0.24 }); tone(ac, out, { freq: E4, t: t + 0.09, dur: 0.26, type: 'sine', gain: 0.24, glideTo: E4 * 0.85 }) },
  undeafen: (ac, out, t) => { tone(ac, out, { freq: E4, t, dur: 0.12, type: 'sine', gain: 0.22 }); tone(ac, out, { freq: A4, t: t + 0.09, dur: 0.24, type: 'sine', gain: 0.24, partial: 0.1 }) },
  // others — brighter sine, higher, quieter so they recede behind your own actions
  peerJoin: (ac, out, t) => { tone(ac, out, { freq: D6, t, dur: 0.09, type: 'sine', gain: 0.12 }); tone(ac, out, { freq: G6, t: t + 0.08, dur: 0.18, type: 'sine', gain: 0.13 }) },
  peerLeave: (ac, out, t) => { tone(ac, out, { freq: G6, t, dur: 0.09, type: 'sine', gain: 0.12 }); tone(ac, out, { freq: D6, t: t + 0.08, dur: 0.2, type: 'sine', gain: 0.11 }) },
  // the memorable one — three soft, muffled raps, like a knock through a door
  knock: (ac, out, t) => { rap(ac, out, t, 1); rap(ac, out, t + 0.19, 0.92); rap(ac, out, t + 0.38, 0.85) },
  // chat — a tiny, near-subliminal tick
  message: (ac, out, t) => { tone(ac, out, { freq: D6, t, dur: 0.07, type: 'sine', gain: 0.1 }) },
  // mention — a gentle two-note bell, more present than a plain message
  mention: (ac, out, t) => { tone(ac, out, { freq: E5, t, dur: 0.16, type: 'sine', gain: 0.18, partial: 0.18 }); tone(ac, out, { freq: A5, t: t + 0.11, dur: 0.34, type: 'sine', gain: 0.18, partial: 0.18 }) },
  // generic good-news (knock accepted, grab) — bright ascending major triad
  notify: (ac, out, t) => { tone(ac, out, { freq: C5, t, dur: 0.1, type: 'sine', gain: 0.16, partial: 0.12 }); tone(ac, out, { freq: E5, t: t + 0.08, dur: 0.1, type: 'sine', gain: 0.16, partial: 0.12 }); tone(ac, out, { freq: G5, t: t + 0.16, dur: 0.3, type: 'sine', gain: 0.17, partial: 0.14 }) },
}

/* Labelled catalog for the dev soundboard — grouped the way the cues are
   designed (your actions / other people / signals), so it stays in step with the
   cue table above without the panel having to know how anything is synthesized. */
export const SFX_CATALOG: { group: string; items: { name: Sfx; label: string; desc: string }[] }[] = [
  { group: 'You', items: [
    { name: 'selfJoin', label: 'Join', desc: 'you enter a call' },
    { name: 'selfLeave', label: 'Leave', desc: 'you leave a call' },
    { name: 'mute', label: 'Mute', desc: 'mic off' },
    { name: 'unmute', label: 'Unmute', desc: 'mic on' },
    { name: 'deafen', label: 'Deafen', desc: 'output off' },
    { name: 'undeafen', label: 'Undeafen', desc: 'output on' },
  ] },
  { group: 'Others', items: [
    { name: 'peerJoin', label: 'Peer join', desc: 'someone arrives' },
    { name: 'peerLeave', label: 'Peer leave', desc: 'someone drops' },
  ] },
  { group: 'Signals', items: [
    { name: 'knock', label: 'Knock', desc: 'someone knocks' },
    { name: 'message', label: 'Message', desc: 'incoming chat' },
    { name: 'mention', label: 'Mention', desc: 'you’re @-mentioned' },
    { name: 'notify', label: 'Notify', desc: 'knock accepted / grab' },
  ] },
]

function emit(name: Sfx, vol: number): void {
  if (vol <= 0) return
  const a = audio()
  if (!a) return
  a.out.gain.value = vol
  const fn = cues[name]
  if (fn) {
    try { fn(a.ac, a.out, a.ac.currentTime + LEAD) } catch (e) { console.warn('[sfx]', name, e) }
  }
}

/** Play a named cue. No-op when sound effects are off; volume rides prefs live.
 *  Returns ~how long the cue holds the speakers (ms, including the scheduling
 *  lead), or 0 if nothing played — callers can wait this out to avoid clipping it. */
export function playSfx(name: Sfx): number {
  let vol = 0.7
  try {
    const prefs = usePrefs()
    if (!prefs.sfx) return 0
    vol = prefs.sfxVolume
  } catch { /* prefs not ready (pre-boot) — fall back to a sane default */ }
  if (vol <= 0) return 0

  const now = Date.now()
  if (lastAt[name] && now - lastAt[name] < MIN_GAP_MS) return 0
  lastAt[name] = now
  emit(name, vol)
  return CUE_MS[name] + LEAD * 1000
}

/** Audition a cue from the dev soundboard — ignores the on/off toggle and the
 *  throttle so cues can be triggered back-to-back. */
export function previewSfx(name: Sfx): void {
  let vol = 0.7
  try { const v = usePrefs().sfxVolume; if (v > 0) vol = v } catch { /* pre-boot default */ }
  emit(name, vol)
}
